import { formatTimeAgo, parseCalendarDate, relativeCalendarDay } from './calendar-date';

/**
 * The Recent Activity feed, shared by the office screen (`SafetyScreen`) and
 * the field screen (`FieldSafetyScreen`).
 *
 * ── WHY THIS IS A MODULE AND NOT TWO COPIES ─────────────────────────────────
 * Both screens render the same feed from the same endpoints, and every bug
 * this file exists to prevent has been a divergence between them:
 *
 *   - the field screen labelled incidents "Equipment inspection passed" and
 *     rendered them GREEN, for as long as the office screen had it right;
 *   - the field screen never sorted, so incidents — pushed last, then sliced —
 *     were displaced by routine items;
 *   - `formatTimeAgo` existed twice, byte-identical, one copy per screen.
 *
 * Each was found separately. They are one fault: a feed with two
 * implementations. New entry kinds go here, not in a screen.
 */

export type FeedTone = 'success' | 'warning' | 'danger';

/**
 * `incident` is a reported safety incident. `upcoming` is a DEADLINE that has
 * not arrived yet — today, a cert expiry warning. `routine` is everything else:
 * meetings, JSAs, and cert warnings for certs that have already lapsed.
 *
 * This is NOT a synonym for `tone`. A cert that has expired is `danger` and
 * still `routine`; the distinction is what the row is ABOUT, not how alarming
 * it looks, because the reservation rule below has to survive someone
 * restyling a tone.
 *
 * The line between `upcoming` and `routine` is the SIGN of the interval, not
 * the subject: the same certification is `upcoming` the day before it lapses
 * and `routine` the day after. A lapsed cert is a past event and is ranked and
 * dated as one.
 */
export type FeedKind = 'incident' | 'upcoming' | 'routine';

export interface SafetyFeedEntry {
  type: FeedTone;
  text: string;
  /** Human label — "5 hours ago", "yesterday", "today", "". */
  time: string;
  /**
   * THE INSTANT THIS ROW DESCRIBES, in epoch ms — for a past event, the one
   * `time` names; for an `upcoming` row, the deadline named in `text`. The
   * invariant is that the sort key is a moment the row itself states, never one
   * invented to position it. Entries with nothing to describe use 0 and sort
   * last within their kind.
   *
   * A forward-looking row therefore carries a FUTURE ts and an empty `time`.
   * It has no age, and inventing one — this was `now - 5 hours`, for years —
   * produces a label that is false and, since the sort landed, a position that
   * is arbitrary: every warning shared the one fabricated instant, so their
   * order fell through to whatever the API happened to list first.
   */
  ts: number;
  kind: FeedKind;
}

/** Lower sorts first. Incidents outrank everything — see `orderSafetyFeed`. */
const KIND_RANK: Record<FeedKind, number> = { incident: 0, upcoming: 1, routine: 2 };


/**
 * Resolve a label and a sort key from whichever of the two a row actually has.
 *
 * `createdAt` is an INSTANT; `meetingDate` / `incidentDate` are CALENDAR DAYS.
 * The `||` this replaced handed whichever survived to `formatTimeAgo`, which
 * reads instants — so a day-only row parsed as UTC midnight, announced the
 * wrong age AND sorted below same-day instants that were genuinely older.
 */
export function feedTime(
  instant: string | undefined | null,
  calendarDay: string | undefined | null,
  /** The TENANT's zone (`useTenantTimeZone()`): whose "today" a day-only row is
   *  measured against, and whose day an instant older than a week is shown as. */
  timeZone: string | null,
): { label: string; ts: number } {
  if (instant) {
    const dt = new Date(instant);
    if (!Number.isNaN(dt.getTime())) return { label: formatTimeAgo(instant, timeZone), ts: dt.getTime() };
  }
  const day = parseCalendarDate(calendarDay);
  if (day) return { label: relativeCalendarDay(calendarDay, timeZone) ?? '', ts: day.getTime() };
  return { label: '', ts: 0 };
}

/**
 * Order the feed and cut it to `limit`.
 *
 * ── AN INCIDENT IS NEVER DISPLACED BY A ROUTINE ITEM ────────────────────────
 * Sorting by recency alone does NOT give this. The field screen's feed can
 * hold three meetings, an UNBOUNDED number of cert warnings, two JSAs and two
 * incidents, and it is cut to five — so five routine items that happen to be
 * newer than the incident push the incident out on pure recency, which is the
 * same silent disappearance the sort was added to fix.
 *
 * So the sort is two-key: kind first, then by time inside each kind. An
 * incident can only ever be displaced by another, newer incident.
 *
 * ── A DEADLINE IS NOT AN EVENT, AND SORTS THE OTHER WAY ─────────────────────
 * `upcoming` rows are ordered SOONEST-first, which is ascending, because the
 * most urgent deadline is the nearest one — the opposite of past events, where
 * the most relevant is the most recent. Running them newest-first would put the
 * cert expiring in 89 days above the one expiring tomorrow.
 *
 * They are ranked above `routine` rather than interleaved by recency because
 * they have no recency to interleave on: a warning about next week is not older
 * or newer than a meeting that happened this morning. Giving them a position is
 * the honest answer; the alternative is to invent an instant, which is the bug
 * this replaced.
 *
 * This is the one place the two screens' behaviour is DELIBERATELY stronger
 * than the office screen's old inline `sort((a, b) => b._ts - a._ts)`. That
 * sort was the right shape and the wrong number of keys; the office screen has
 * the same latent bug (≤3 meetings + ≤3 JSAs + ≤2 incidents against a cut of
 * six) and inherits the fix by using this function rather than its own.
 *
 * Non-mutating: the caller's array is left alone, because `recentActivity` is
 * rebuilt on every render and an in-place sort of a memoised array is a bug
 * waiting for the first person to memoise it.
 */
export function orderSafetyFeed<T extends { ts: number; kind: FeedKind }>(
  entries: T[],
  limit: number,
): T[] {
  return [...entries]
    .sort((a, b) => {
      const byKind = KIND_RANK[a.kind] - KIND_RANK[b.kind];
      if (byKind !== 0) return byKind;
      return a.kind === 'upcoming' ? a.ts - b.ts : b.ts - a.ts;
    })
    .slice(0, limit);
}
